import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useControlled } from './useControlled';
import { TabValue } from '../types/TabsConfig';

export type TabsContextType = {
  value: TabValue;
  onValueChange: (value: TabValue) => void;
};

const TabsContext = createContext<TabsContextType | null>(null);

export type TabsProviderProps = {
  children: ReactNode;
  value?: TabValue;
  defaultValue?: TabValue;
  onValueChange?: (value: any) => void;
};

export const TabsProvider: React.FC<TabsProviderProps> = ({
  children,
  value: controlledValue,
  defaultValue,
  onValueChange,
}) => {
  const [value, setValue] = useControlled<TabValue>({
    controlled: controlledValue,
    default: defaultValue,
  });

  const handleValueChange = useCallback(
    (newValue: TabValue) => {
      setValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange],
  );

  const contextValue: TabsContextType = useMemo(
    () => ({
      value,
      onValueChange: handleValueChange,
    }),
    [value, handleValueChange],
  );

  return <TabsContext value={contextValue}>{children}</TabsContext>;
};

export const useTabsContext = (): TabsContextType => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider');
  }
  return context;
};
